import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Company } from '../../../models/company.model';
import { Employee } from '../../../models/employee.model';
import EmployeeDetailsCard from '../../../common/employee-details-card/EmployeeDetailsCard';
import EmploymentCard from '../../../common/employment-card/EmploymentCard';
import QualificationsCard from '../../../common/qualifications-card/QualificationsCard';
import EmergencyContactCard from '../../../common/emergency-contact-card/EmergencyContactCard';
import FormUpload from '../../../common/form-upload/FormUpload';
import { NWClient } from '../../../client/NWClient';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { selectUser } from '../../../slices/user/userSlice';
import { fetchDocuments, selectAllDocuments } from '../../../slices/documents/documentsSlice';
import { RootState } from '../../../store';

const ID_CARDS_WORKFLOW_KEY = 'tagsLabels.documentWorkflow.employment.idCardsPassportsAndLicenses';

interface DetailsTabsProps {
  company: Company;
  employee: Employee;
  key: string;
}

const DetailsTabs: React.FC<DetailsTabsProps> = ({ company, employee }) => {
  const { t } = useTranslation();
  const { employeeId } = useParams();
  const currentUser = useAppSelector(selectUser);
  const workflowLabel = t(ID_CARDS_WORKFLOW_KEY);

  const dispatch = useAppDispatch();
  const documents = useAppSelector(selectAllDocuments);
  const docsStatus = useAppSelector((state) => state.documents.status);
  const docsLoading = docsStatus === 'loading';
  const taskIds = useAppSelector((state: RootState) => state.tasks.taskIds);

  const [isUploadOpen, setIsUploadOpen] = useState(false);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const fetchIdDocuments = useCallback(() => {
    if (!currentUser?.id) return;
    if (!(taskIds && taskIds.length === 0)) return;
    dispatch(
      fetchDocuments({
        data: {
          user: currentUser.id,
          category: 'employment',
          workflow: workflowLabel,
          ...(employeeId ? { profile: Number(employeeId) } : {}),
        },
      })
    );
  }, [dispatch, currentUser?.id, employeeId, workflowLabel, taskIds]);

  useEffect(() => {
    fetchIdDocuments();
  }, [fetchIdDocuments]);

  const handleUploadCancel = () => {
    setIsUploadOpen(false);
    //fetchIdDocuments();
  };

  return (
    <>
      <Grid component='div' container wrap='wrap' style={{ rowGap: '16px', paddingBottom: '16px' }}>
        <Grid component='div' size={{ xs: 12 }}>
          <EmployeeDetailsCard employee={employee} company={company} />
        </Grid>

        <Grid component='div' size={{ xs: 12 }}>
          <EmploymentCard employee={employee} />
        </Grid>

        <Grid component='div' size={{ xs: 12 }}>
          <QualificationsCard employee={employee} />
        </Grid>

        <Grid component='div' size={{ xs: 12 }}>
          <EmergencyContactCard employee={employee} />
        </Grid>

        {/* ID Cards, Passports & Licenses section */}
        <Grid component='div' size={{ xs: 12 }}>
          <Card style={{ overflow: 'visible' }}>
            <CardContent>
              <Grid
                component='div'
                container
                style={{
                  alignItems: 'center',
                  paddingBottom: '16px',
                  minHeight: '60px',
                  flexWrap: 'nowrap',
                }}
              >
                <Grid
                  component='div'
                  size={{ xs: 11 }}
                  container
                  style={{ alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}
                >
                  <Grid
                    component='div'
                    sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                  >
                    <BadgeOutlinedIcon />
                  </Grid>
                  <Grid component='div'>
                    <Typography variant='h6'>{workflowLabel}</Typography>
                  </Grid>
                </Grid>
                <Grid
                  component='div'
                  size={{ xs: 1 }}
                  style={{ justifyContent: 'flex-end', overflow: 'visible' }}
                  container
                >
                  <Box sx={{ position: 'relative' }}>
                    <IconButton
                      id='employeeProfilesDetailsTabUploadIconBtn'
                      onClick={() => setIsUploadOpen((prev) => !prev)}
                      title={t('buttons.upload')}
                    >
                      <AddCircleOutlineOutlinedIcon id='employeeProfilesDetailsTabUploadIconBtnAddCircleOutlineIcon' />
                    </IconButton>
                    {isUploadOpen && (
                      <Box sx={{ position: 'absolute', right: '100%', top: 0, zIndex: 1005 }}>
                        <FormUpload
                          categoryValue='employment'
                          onCancel={handleUploadCancel}
                          employees={employee ? [employee] : []}
                          workflow={workflowLabel}
                          className='show__left__top'
                          accept='image/png, image/jpeg, application/pdf, image/heic'
                          supportedFormatsKey='messages.supportedFormatsImages'
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {docsLoading ? (
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: '16px',
                    paddingBottom: '16px',
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : documents.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  {t('messages.noDocuments', 'No documents uploaded yet.')}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {documents.map((doc) => (
                    <ListItem key={doc.id} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <InsertDriveFileOutlinedIcon fontSize='small' />
                      </ListItemIcon>
                      <ListItemText
                        sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                        primary={
                          <Typography
                            id={`employeeProfilesDetailsTabDocumentLink_${doc?.id}`}
                            variant='body2'
                            component='span'
                            onClick={() =>
                              NWClient.downloadDocument(doc.id, doc.name ?? String(doc.id))
                            }
                            sx={{
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              color: 'primary.main',
                            }}
                          >
                            {doc.name ?? String(doc.id)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default DetailsTabs;
